import React, { Fragment, ReactNode } from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { IAppContext } from '@kurtosys/types/helpers/AnalyticsHelper/IAppContext.js';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';

import { appendChildGroupFunctions } from '../../../../../shared/appendChildGroupFunctions.js';
import { AnalyticsContext, InjectedStyledComponent } from '../../../../../shared/index.js';
import { Checkbox } from '../../../Checkbox/Checkbox.js';
import { ICheckboxProps } from '../../../Checkbox/models/index.js';
import { Label } from '../../../Label/Label.js';
import { ToggleButton } from '../../../ToggleButton/ToggleButton.js';
import { ITableEvents } from '../../models/index.js';
import { ITableGroup } from '../../models/ITableGroup.js';
import { ITableRow } from '../../models/ITableRow.js';
import { TableComponentBase } from '../TableComponentBase.js';

import { ICardGroupingConfiguration, ICardGroupingProps, ICardGroupingState } from './models/index.js';
import {
	CARD_GROUPING_CATEGORY_KEY,
	CARD_GROUPING_COMPONENT_KEY,
	CARD_GROUPING_PARENT_COMPONENT_KEY,
} from './CardGrouping.meta.js';

type TRowProps = ICardGroupingProps & React.TableHTMLAttributes<HTMLTableRowElement>;
export class CardGrouping extends TableComponentBase<TRowProps, ICardGroupingState, ICardGroupingConfiguration> {
	categoryKey = CARD_GROUPING_CATEGORY_KEY;
	componentKey = CARD_GROUPING_COMPONENT_KEY;
	analyticsHelper: common.helpers.AnalyticsHelper<IAppContext> | undefined;

	constructor(props: TRowProps) {
		super(props);

		makeObservable(this, {
			childRows: observable.ref,
			expanded: observable.ref,
			fetchingChildren: observable.ref,
			toggleExpanded: action,
			onToggleChange: action,
			fetchChildren: action,
			isLastGrouping: computed,
			childGroup: computed,
			row: computed,
			rowIndex: computed,
			grouping: computed,
			columns: computed,
			cellDescribedby: computed,
			heading: computed,
			checkbox: computed,
		});

		this.initialize();
	}

	private initialize = () => {
		if (this.grouping) {
			this.toggleExpanded(!!this.row._expanded);
		}
	};

	get parentComponentKey(): string | undefined {
		return CARD_GROUPING_PARENT_COMPONENT_KEY;
	}

	get tableEventsKey(): keyof ITableEvents {
		return 'cardGrouping';
	}

	public childRows: ITableRow[] = [];

	public expanded = false;

	public fetchingChildren = false;

	public toggleExpanded = (explicitValue?: boolean) => {
		if (!this.isLastGrouping) {
			const value = typeof explicitValue === 'boolean' ? explicitValue : !this.expanded;
			const isChange = this.expanded !== value;
			if (isChange) {
				runInAction(() => this.onToggleChange(value, this.row));
			}
			this.expanded = value;
			if (this.expanded) {
				runInAction(() => this.fetchChildren());
			}
		}
	};

	public onExpand = (row: ITableRow): void => {
		if (this.grouping && this.grouping.onExpand) {
			this.grouping.onExpand(row, this.columns);
		}
	};

	public onCollapse = (row: ITableRow): void => {
		if (this.grouping && this.grouping.onCollapse) {
			this.grouping.onCollapse(row, this.columns);
		}
	};

	public onToggleChange = (isExpanded: boolean, row: ITableRow): void => {
		const grouping = this.grouping;
		if (grouping && grouping.onToggleChange) {
			grouping.onToggleChange(isExpanded, row);
		}
		const { analyticsContext, suppressAnalytics } = this.props;
		if (this.analyticsHelper && !suppressAnalytics) {
			let context = { isExpanded: isExpanded };
			if (analyticsContext) {
				context = { ...AnalyticsContext, ...context };
			}
			this.analyticsHelper.logEvent({
				event: 'cardgroup_toggle',
				eventType: 'click',
				context: context,
			});
		}
	};

	public fetchChildren = async () => {
		const grouping = this.grouping;
		if (
			grouping &&
			(grouping.getRowsQuery || grouping.fetchChildren) &&
			(!this.childRows || this.childRows.length === 0)
		) {
			this.fetchingChildren = true;
			const { queryStore } = this.table.props;
			if (queryStore) {
				let overridingExecutionOptions = {};
				if (grouping.onFetchChildren) {
					overridingExecutionOptions = await grouping.onFetchChildren(this.row, queryStore);
				}
				if (grouping.fetchChildren) {
					runInAction(async () => {
						if (grouping && grouping.fetchChildren) {
							this.childRows = await grouping.fetchChildren(this.row, this.columns);
						}
					});
				} else {
					let queryResponse = queryStore.query(grouping.getRowsQuery, {
						...overridingExecutionOptions,
						responseContext: { grouping, row: this.row },
					});
					if (grouping.orchestrateChildRows) {
						queryResponse = await grouping.orchestrateChildRows(queryResponse, grouping, this.row);
					}
					runInAction(() => {
						if (queryResponse) {
							this.childRows = queryResponse;
						}
					});
				}
			}
			this.fetchingChildren = false;
		}
	};

	get isLastGrouping(): boolean {
		return (
			!this.grouping || (!this.grouping.getRowsQuery && !this.grouping.fetchChildren) || !this.grouping.childGroup
		);
	}

	get childGroup(): ITableGroup | null {
		if (this.grouping) {
			return appendChildGroupFunctions(this.grouping);
		}
		return null;
	}

	get row(): ITableRow {
		return this.props.row;
	}

	get rowIndex(): number {
		return this.props.rowIndex;
	}

	get grouping(): ITableGroup | undefined {
		return this.props.grouping;
	}

	getClassName = (className?: string, rowIndex?: number, grouping?: ITableGroup) => {
		const classNames = [];
		if (className) {
			classNames.push(className);
		}
		const groupClassName =
			grouping && grouping.id && `ksys-table-row-group-${common.commonUtils.paramCase(grouping.id)}`;
		if (groupClassName) {
			classNames.push(groupClassName);
		}
		if (typeof rowIndex === 'number') {
			const isEven = (rowIndex + 1) % 2 === 0;
			const evenClassName = `ksys-table-row-even`;
			const oddClassName = `ksys-table-row-odd`;
			classNames.push(isEven ? evenClassName : oddClassName);
		}
		return classNames.join(' ');
	};

	get columns() {
		return [...((this.grouping && this.grouping.columns) || this.table.columns)];
	}

	get cellDescribedby() {
		const { row } = this.props;
		const rowColumnIdentifier = this.columns && this.columns.find((column) => column.isRowIdentifier);
		if (rowColumnIdentifier) {
			return `${row._rowKey}_${rowColumnIdentifier.id}`;
		}
	}

	get heading() {
		const { grouping, row } = this.props;
		let headingKey = grouping && grouping.headingKey;
		if (common.commonUtils.isNullOrEmpty(headingKey) && !common.commonUtils.isNullOrEmpty(this.columns)) {
			headingKey = this.columns[0].key;
		}
		return (row as any)[headingKey || 'name'];
	}

	get checkbox() {
		const { grouping } = this.props;
		const checkbox = grouping && grouping.cardGroupingCheckbox;
		if (checkbox && checkbox.show) {
			const checkboxProps: ICheckboxProps = {
				value: this.row._selected,
				onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
					checkbox.onChange(this.row, e.target.checked);
				},
			};
			return <Checkbox {...checkboxProps} />;
		}
	}

	renderToggleButton = (children?: ReactNode) => {
		const { row, grouping } = this.props;
		return (
			<ToggleButton
				onChange={async (active) => {
					if (!row._disabled) {
						row._expanded = active;
						this.toggleExpanded(active);
						if (active) {
							this.onExpand(row);
						} else {
							this.onCollapse(row);
						}
					}
				}}
				active={row._expanded}
				disabled={row._disabled}
				loading={this.fetchingChildren}
				buttonProps={{
					describedby: this.cellDescribedby,
					children,
				}}
				iconActive={{
					asset: grouping?.expandButtonAssets?.activeAsset
						? grouping.expandButtonAssets.activeAsset
						: 'base.toggleButton.activeMobile',
					accessibilityText: 'Collapse',
				}}
				iconInactive={{
					asset: grouping?.expandButtonAssets?.inactiveAsset
						? grouping.expandButtonAssets.inactiveAsset
						: 'base.toggleButton.inactiveMobile',
					accessibilityText: 'Expand',
				}}
				iconDisabledInactive={{
					asset: grouping?.expandButtonAssets?.disabledAsset
						? grouping.expandButtonAssets.disabledAsset
						: 'base.toggleButton.disabledInactiveMobile',
					accessibilityText: 'Disabled Expand',
				}}
				suppressAnalytics={true}
			/>
		);
	};

	generateButtonLabel = () => {
		const { grouping, row } = this.props;
		if (grouping && grouping.headingQueries) {
			const queryStore = this.table.props.queryStore;
			if (queryStore) {
				return grouping.headingQueries.map((headingQuery, index) => {
					return <span key={index}>{queryStore.query(headingQuery, { responseContext: row })}</span>;
				});
			}
		}
	};

	render() {
		const {
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			children,
			className,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			configuration,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			componentStyle,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			componentStyleProps,
			table,
			row,
			rowIndex,
			grouping,
			...otherProps
		} = this.props;
		const events = this.getEvents(this.eventsForElement, this.row, this.rowIndex);
		const cardGroupingClassName = this.getClassName(className, rowIndex, grouping);

		const hideToggle =
			grouping &&
			grouping.hideToggle &&
			grouping.hideToggleConditional &&
			grouping.hideToggle(row, grouping.hideToggleConditional);
		const disableToggle =
			grouping &&
			grouping.disableToggle &&
			grouping.disableToggleConditional &&
			grouping.disableToggle(row, grouping.disableToggleConditional);
		if (disableToggle) {
			row._disabled = true;
		}

		const { childRows, table: _table, childGroup, expanded, isLastGrouping, fetchingChildren } = this;

		return (
			<AnalyticsContext.Consumer>
				{(analyticsHelper) => {
					this.analyticsHelper = analyticsHelper;
					return (
						<Fragment>
							{grouping?.getRowsQuery && (
								<div className={cardGroupingClassName} {...events} {...otherProps}>
									{this.checkbox}
									<Label>{this.heading || table.props.placeholder}</Label>
									{!hideToggle && this.renderToggleButton()}
								</div>
							)}
							{grouping?.fetchChildren &&
								!hideToggle &&
								this.renderToggleButton(this.generateButtonLabel())}
							{!isLastGrouping &&
								!fetchingChildren &&
								expanded &&
								childRows &&
								childRows.length > 0 &&
								_table.renderCards(childRows, childGroup)}
						</Fragment>
					);
				}}
			</AnalyticsContext.Consumer>
		);
	}
}

const StyledCardGrouping = InjectedStyledComponent(
	observer(CardGrouping),
	CARD_GROUPING_CATEGORY_KEY,
	CARD_GROUPING_PARENT_COMPONENT_KEY,
	[CARD_GROUPING_COMPONENT_KEY],
);
export default StyledCardGrouping;
